import { faArrowRotateRight } from '@fortawesome/pro-solid-svg-icons';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import styled, { createGlobalStyle, css } from 'styled-components';

import { FontAwesomeIcon } from 'atoms/icons';
import { Horizontal } from 'atoms/layout/flex';
import { LoadingIndicator } from 'atoms/loader/LoadingIndicator';
import { useIsMobileApp } from 'hooks/useIsMobileApp';
import { clamp } from 'lib/math';

// Show loading indicator for at least 500ms
const MIN_LOAD_DURATION = 500;
const FADE_OUT_DURATION = 200;

const MAX_DRAG_DISTANCE = 100;
const MIN_DRAG_DISTANCE = 40;
const REFRESH_THRESHOLD = 75;

const IS_SUPPORTED =
  // We need to intercept pull to refresh
  CSS.supports?.('overscroll-behavior', 'none') &&
  typeof document.body.animate === 'function' &&
  window.matchMedia('(pointer: coarse)').matches;

const RefetchIndicator = styled.div<{ $fetching: boolean }>`
  position: relative;
  display: flex;
  z-index: 0;
  pointer-events: none;
  & > * {
    position: absolute;
    opacity: 0;
    z-index: 1;
    width: calc(5 * var(--unit));
    height: calc(5 * var(--unit));
    color: var(--c-white);
    font-size: 1em;
    top: calc(
      (-1 * var(--double-and-a-half-unit)) + (-1px * ${MAX_DRAG_DISTANCE} / 2)
    );
    left: 0;
    right: 0;
    margin: auto;
    transform-origin: center;
    & > * {
      position: absolute;
      inset: 0;
    }

    & > *:first-child {
      ${({ $fetching }) =>
        $fetching
          ? css`
              display: none;
            `
          : null}
    }

    & > *:last-child {
      ${({ $fetching }) =>
        !$fetching
          ? css`
              display: none;
            `
          : null}
    }
  }
`;

const DisableNativeRefresh = createGlobalStyle`
  html, body {
    overscroll-behavior: none;
  }
`;

type State = 'idle' | 'preview' | 'fetching';

export const PullToRefetch = ({
  refetch,
  children,
}: {
  refetch: () => Promise<any>;
  children: ReactNode;
}): ReactNode => {
  const containerRef = useRef<HTMLDivElement>(null);
  const indicatorRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<State>('idle');
  const { isMobileApp } = useIsMobileApp();

  // Ensure refetch is a stable reference when called in second useEffect
  const refetchRef = useRef(refetch);
  const isDisabled = !(IS_SUPPORTED || isMobileApp);

  useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch]);

  useEffect(() => {
    const container = containerRef.current;
    const indicator = indicatorRef.current;
    if (isDisabled || !container || !indicator) {
      // Disable for Browsers that cannot intercept overscroll-behavior
      // We need this behavior to intercept pull to refresh.
      // This disables this feature for iOS 15
      // Allow when running in MobileApp as that has no native pull to refresh
      return () => {};
    }

    // Animate values for both divs, ideally we could use a CSS.defineProperty
    // to animate both with one custom property
    // Here, but browser support is lacking (no Firefox & only Safari >= 16.4)
    async function animateTo(value: number, duration: number) {
      return Promise.all(
        [
          container!.animate(
            [
              {
                transform: `translateY(calc(${MAX_DRAG_DISTANCE}px * ${value}))`,
              },
            ],
            { fill: 'forwards', duration }
          ),
          indicator!.animate(
            [{ transform: `scale(${value})`, opacity: `${value}` }],
            { fill: 'forwards', duration }
          ),
        ].map(async animation => animation.finished)
      );
    }
    let startY: number | undefined;
    let lastTouch: number | undefined;
    let refetching = false;

    const touchStart = (e: TouchEvent) => {
      // Do not set startY if currently refetching
      if (!refetching) {
        startY = e.touches[0].pageY;
        lastTouch = startY;
      }
    };
    const touchMove = (e: TouchEvent) => {
      const y = e.touches[0].pageY;
      lastTouch = y;
      if (startY == null) return;

      const diff = document.scrollingElement?.scrollTop === 0 ? y - startY : 0;
      // Ignore small drag distances otherwise inner horizontal scroll content
      // won't scroll on mobile web
      if (Math.abs(diff) < MIN_DRAG_DISTANCE) {
        return;
      }
      const progress = clamp(diff, 0, MAX_DRAG_DISTANCE) / MAX_DRAG_DISTANCE;
      animateTo(progress, 1);

      if (diff > 0) {
        setState('preview');
      } else if (diff <= 0) {
        setState('idle');
      }
    };
    const touchEnd = () => {
      const y = lastTouch;
      if (startY == null || y == null) return;

      const diff = document.scrollingElement?.scrollTop === 0 ? y - startY : 0;
      startY = undefined;

      if (diff > REFRESH_THRESHOLD) {
        setState('fetching');
        refetching = true;

        Promise.all([
          animateTo(1, 50),
          new Promise(r => setTimeout(r, MIN_LOAD_DURATION)),
          refetchRef.current(),
        ]).then(async () => {
          await animateTo(0, FADE_OUT_DURATION);
          setState('idle');
          refetching = false;
        });
      } else {
        animateTo(0, FADE_OUT_DURATION);
      }
    };
    container.addEventListener('touchstart', touchStart, { passive: true });
    container.addEventListener('touchmove', touchMove, { passive: true });
    container.addEventListener('touchend', touchEnd, { passive: true });
    container.addEventListener('touchcancel', touchEnd, { passive: true });
    return () => {
      container.removeEventListener('touchstart', touchStart);
      container.removeEventListener('touchmove', touchMove);
      container.removeEventListener('touchend', touchEnd);
      container.removeEventListener('touchcancel', touchEnd);
    };
  }, [isMobileApp, isDisabled]);

  if (isDisabled) {
    return children;
  }

  return (
    <>
      <DisableNativeRefresh />
      <div style={{ willChange: 'transform' }} ref={containerRef}>
        <RefetchIndicator
          aria-hidden={state !== 'fetching'}
          $fetching={state === 'fetching'}
        >
          <div ref={indicatorRef}>
            <Horizontal
              style={{
                height: '100%',
                width: '100%',
                transform: 'rotate(270deg)',
              }}
              center
            >
              <FontAwesomeIcon icon={faArrowRotateRight} />
            </Horizontal>
            <LoadingIndicator small />
          </div>
        </RefetchIndicator>
        {children}
      </div>
    </>
  );
};
